// Audio engine and pitch detection utilities
// Ported and adapted from triadmusic-tuner-looper

export const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
export const NOTE_NAMES_FLAT = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'Gb', 'G', 'Ab', 'A', 'Bb', 'B'];

export class AudioEngine {
  static audioCtx = null;
  static diffBuffer = new Float32Array(2048);

  static getAudioContext() {
    if (!this.audioCtx) {
      const AudioCtxClass = window.AudioContext || window.webkitAudioContext;
      this.audioCtx = new AudioCtxClass();
    }
    if (this.audioCtx.state === 'suspended') {
      this.audioCtx.resume();
    }
    return this.audioCtx;
  }

  /**
   * Convert frequency in Hz to Note information based on standard A4 reference (default 440 Hz).
   */
  static frequencyToNote(freq, a4 = 440) {
    if (freq <= 0 || isNaN(freq) || !isFinite(freq)) return null;

    // MIDI note number where A4 (440Hz) is 69
    const noteNum = 12 * (Math.log(freq / a4) / Math.log(2)) + 69;
    const roundedMidi = Math.round(noteNum);
    const cents = Math.round((noteNum - roundedMidi) * 100);

    const noteIndex = ((roundedMidi % 12) + 12) % 12;
    const octave = Math.floor(roundedMidi / 12) - 1;
    const targetFreq = a4 * Math.pow(2, (roundedMidi - 69) / 12);

    return {
      name: NOTE_NAMES[noteIndex],
      octave,
      frequency: targetFreq,
      midi: roundedMidi,
      cents,
    };
  }

  /**
   * Convert note name (e.g. 'A', 'C#') and octave to frequency
   */
  static noteToFrequency(noteName, octave, a4 = 440) {
    let index = NOTE_NAMES.indexOf(noteName);
    if (index === -1) {
      index = NOTE_NAMES_FLAT.indexOf(noteName);
    }
    if (index === -1) return 440;

    const midi = (octave + 1) * 12 + index;
    return a4 * Math.pow(2, (midi - 69) / 12);
  }

  /**
   * Calculate cents deviation between two frequencies
   */
  static calculateCentsDifference(currentFreq, targetFreq) {
    if (currentFreq <= 0 || targetFreq <= 0) return 0;
    return Math.round(1200 * (Math.log(currentFreq / targetFreq) / Math.log(2)));
  }

  /**
   * High performance Yin pitch detection algorithm.
   */
  static detectPitchYIN(buffer, sampleRate, threshold = 0.20) {
    const bufferSize = buffer.length;

    // 1. Calculate RMS volume (energy)
    let sumSquares = 0;
    for (let i = 0; i < bufferSize; i++) {
      const val = buffer[i];
      sumSquares += val * val;
    }
    const rms = Math.sqrt(sumSquares / bufferSize);

    if (rms < 0.002) {
      return { frequency: 0, clarity: 0, rms };
    }

    // 2. Difference function
    const minPeriod = Math.max(16, Math.floor(sampleRate / 1400)); // ~1400 Hz max
    const maxPeriod = Math.floor(sampleRate / 35);   // ~35 Hz min
    const halfBufferSize = Math.floor(bufferSize / 2);
    const searchLimit = Math.min(maxPeriod, halfBufferSize);

    const diff = this.diffBuffer;
    diff[0] = 1;

    let runningSum = 0;
    for (let tau = 1; tau < searchLimit; tau++) {
      let deltaSum = 0;
      for (let i = 0; i < halfBufferSize; i++) {
        const delta = buffer[i] - buffer[i + tau];
        deltaSum += delta * delta;
      }
      runningSum += deltaSum;
      diff[tau] = runningSum > 0 ? (deltaSum * tau) / runningSum : 1;
    }

    // 3. Absolute threshold
    let bestTau = -1;
    for (let tau = minPeriod; tau < searchLimit; tau++) {
      if (diff[tau] < threshold) {
        while (tau + 1 < searchLimit && diff[tau + 1] < diff[tau]) {
          tau++;
        }
        bestTau = tau;
        break;
      }
    }

    if (bestTau === -1) {
      // Find global minimum if no valley below threshold
      let minVal = 1;
      for (let tau = minPeriod; tau < searchLimit; tau++) {
        if (diff[tau] < minVal) {
          minVal = diff[tau];
          bestTau = tau;
        }
      }
      if (minVal > 0.40) {
        return { frequency: 0, clarity: 0, rms };
      }
    }

    // 4. Parabolic interpolation for sub-bin precision
    const s0 = diff[bestTau - 1] || diff[bestTau];
    const s1 = diff[bestTau];
    const s2 = diff[bestTau + 1] || diff[bestTau];
    const denom = 2 * (2 * s1 - s2 - s0);
    const delta = Math.abs(denom) > 1e-10 ? (s2 - s0) / denom : 0;
    const refinedTau = bestTau + delta;

    const frequency = sampleRate / refinedTau;
    const clarity = Math.max(0, Math.min(1, 1 - (diff[bestTau] || 0)));

    return { frequency, clarity, rms };
  }

  /**
   * Play a reference synth tone
   */
  static playTone(freq, durationSeconds = 2.0, type = 'sine', volume = 0.25) {
    const ctx = this.getAudioContext();
    const now = ctx.currentTime;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = type;
    osc.frequency.setValueAtTime(freq, now);

    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.linearRampToValueAtTime(volume, now + 0.04);
    gain.gain.exponentialRampToValueAtTime(volume * 0.5, now + 0.3);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + durationSeconds);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(now);
    osc.stop(now + durationSeconds);

    return () => {
      try {
        gain.gain.cancelScheduledValues(ctx.currentTime);
        gain.gain.setValueAtTime(gain.gain.value, ctx.currentTime);
        gain.gain.linearRampToValueAtTime(0.0001, ctx.currentTime + 0.05);
        setTimeout(() => osc.stop(), 60);
      } catch {
        // ignore
      }
    };
  }
}

/**
 * Standard tuning presets across instruments
 */
export const TUNING_PRESETS = [
  {
    id: 'guitar-standard',
    name: 'Padrão (E A D G B E)',
    instrument: 'guitar',
    instrumentLabel: 'Violão / Guitarra',
    strings: [
      { note: 'E', octave: 4, frequency: 329.63, label: '1ª (e)', stringNumber: 1 },
      { note: 'B', octave: 3, frequency: 246.94, label: '2ª (B)', stringNumber: 2 },
      { note: 'G', octave: 3, frequency: 196.00, label: '3ª (G)', stringNumber: 3 },
      { note: 'D', octave: 3, frequency: 146.83, label: '4ª (D)', stringNumber: 4 },
      { note: 'A', octave: 2, frequency: 110.00, label: '5ª (A)', stringNumber: 5 },
      { note: 'E', octave: 2, frequency: 82.41, label: '6ª (E)', stringNumber: 6 },
    ],
  },
  {
    id: 'guitar-drop-d',
    name: 'Drop D (D A D G B E)',
    instrument: 'guitar',
    instrumentLabel: 'Violão / Guitarra',
    strings: [
      { note: 'E', octave: 4, frequency: 329.63, label: '1ª (e)', stringNumber: 1 },
      { note: 'B', octave: 3, frequency: 246.94, label: '2ª (B)', stringNumber: 2 },
      { note: 'G', octave: 3, frequency: 196.00, label: '3ª (G)', stringNumber: 3 },
      { note: 'D', octave: 3, frequency: 146.83, label: '4ª (D)', stringNumber: 4 },
      { note: 'A', octave: 2, frequency: 110.00, label: '5ª (A)', stringNumber: 5 },
      { note: 'D', octave: 2, frequency: 73.42, label: '6ª (D)', stringNumber: 6 },
    ],
  },
  {
    id: 'bass-standard',
    name: 'Baixo 4 Cordas (E A D G)',
    instrument: 'bass',
    instrumentLabel: 'Contrabaixo',
    strings: [
      { note: 'G', octave: 2, frequency: 98.00, label: '1ª (G)', stringNumber: 1 },
      { note: 'D', octave: 2, frequency: 73.42, label: '2ª (D)', stringNumber: 2 },
      { note: 'A', octave: 1, frequency: 55.00, label: '3ª (A)', stringNumber: 3 },
      { note: 'E', octave: 1, frequency: 41.20, label: '4ª (E)', stringNumber: 4 },
    ],
  },
  {
    id: 'ukulele-standard',
    name: 'Ukulele Soprano/Concerto (G C E A)',
    instrument: 'ukulele',
    instrumentLabel: 'Ukulele',
    strings: [
      { note: 'A', octave: 4, frequency: 440.00, label: '1ª (A)', stringNumber: 1 },
      { note: 'E', octave: 4, frequency: 329.63, label: '2ª (E)', stringNumber: 2 },
      { note: 'C', octave: 4, frequency: 261.63, label: '3ª (C)', stringNumber: 3 },
      { note: 'G', octave: 4, frequency: 392.00, label: '4ª (G)', stringNumber: 4 },
    ],
  },
  {
    id: 'cavaquinho-standard',
    name: 'Cavaquinho (D G B D)',
    instrument: 'cavaquinho',
    instrumentLabel: 'Cavaquinho',
    strings: [
      { note: 'D', octave: 4, frequency: 293.66, label: '1ª (D)', stringNumber: 1 },
      { note: 'B', octave: 3, frequency: 246.94, label: '2ª (B)', stringNumber: 2 },
      { note: 'G', octave: 3, frequency: 196.00, label: '3ª (G)', stringNumber: 3 },
      { note: 'D', octave: 3, frequency: 146.83, label: '4ª (D)', stringNumber: 4 },
    ],
  }
];
